/**
 * When should the Android background-location foreground service run?
 *
 * Running the service keeps GPS alive screen-off (so alarms still fire and the
 * track has no gaps), but it costs battery and shows a persistent notification,
 * so we only run it when there's a reason to. These are the pure decision rules,
 * kept out of the React tree so they're unit-testable; the runner assembles the
 * inputs from the stores and acts on the answer.
 *
 * The rule, in words: the service needs the phone's real GPS, so it only ever
 * runs on a native build with the Device-GPS source selected. Given that, it runs
 * whenever the anchor watch is down, OR a safety alarm that could actually fire in
 * the background is armed, OR the user has flipped the manual "keep GPS alive in
 * the background" override. The moment the last of those clears, it stops.
 */
import type { AlarmsConfig } from '@/store/alarms';

/** How many clearing bearings / clearing depths the user has actually defined. */
export interface AlarmArmingCounts {
  clearingCount: number;
  clearingDepthCount: number;
}

/**
 * Whether any background-relevant safety alarm is armed. An alarm counts only if
 * it's switched on AND has something to act on: the clearing guards default on
 * but do nothing until the user places a clearing line, so they don't arm the
 * service on their own (which would run it flat for no benefit). The rest
 * (proximity, depth, grounding, tide height, AIS) default off, so with a fresh
 * config nothing here arms the service.
 */
export function anySafetyAlarmArmed(config: AlarmsConfig, counts: AlarmArmingCounts): boolean {
  return (
    config.proximityEnabled ||
    config.depthEnabled ||
    config.groundingEnabled ||
    config.tideHeightEnabled ||
    config.aisProximityEnabled ||
    config.aisCpaEnabled ||
    (config.clearingEnabled && counts.clearingCount > 0) ||
    (config.clearingDepthEnabled && counts.clearingDepthCount > 0)
  );
}

/** Everything the decision needs, gathered from the stores by the runner. */
export interface ServiceDecisionInputs {
  /** Running inside the installed Android app (the plugin only works there). */
  isNative: boolean;
  /** The Device-GPS source is selected (not the simulator). */
  usingDeviceGps: boolean;
  /** The anchor watch is down (anchor position set). */
  anchorWatchArmed: boolean;
  /** A background-relevant safety alarm is armed (see anySafetyAlarmArmed). */
  safetyAlarmArmed: boolean;
  /** The user forced the service on with the manual override toggle. */
  manualOverride: boolean;
}

/**
 * The single source of truth for "should the service be running right now?".
 * The runner calls this whenever any input changes and starts or stops the
 * service to match.
 */
export function serviceShouldRun(i: ServiceDecisionInputs): boolean {
  if (!i.isNative || !i.usingDeviceGps) return false;
  return i.anchorWatchArmed || i.safetyAlarmArmed || i.manualOverride;
}

/** A short reason the service is running, for the persistent notification / UI. */
export function serviceRunReason(i: ServiceDecisionInputs): string | null {
  if (!serviceShouldRun(i)) return null;
  if (i.anchorWatchArmed) return 'Anchor watch active';
  if (i.safetyAlarmArmed) return 'Safety alarm armed';
  return 'Background GNSS kept on';
}
